import type { Components } from '@mui/material'

export const OmuiCountBadge: NonNullable<Components['OmuiCountBadge']> = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape['radius-sm'],
      display: 'inline-flex',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(0, 1),
      paddingInlineEnd: `calc(${theme.spacing(1)} - ${theme.typography['extraSmall-semiBold'].letterSpacing})`, // Trim trailing letter-spacing; indenting would widen an auto-width badge.
      minWidth: `calc(${theme.spacing(2)} + 6px)`, // 6px for content
      fontVariantNumeric: 'tabular-nums',
      textAlign: 'center',
      backgroundColor: theme.palette.bg.neutral.strong,
      // Need this to override specificity from MUI Chip
      '&&&': {
        ...theme.typography['extraSmall-semiBold'],
        color: theme.palette.text.inverted,
      },
    }),
  },
  variants: [
    {
      props: { color: 'secondary' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.brand.default,
        '&&&': {
          color: theme.palette.text.brand.onBrand,
        },
      }),
    },
    {
      props: { color: 'success' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.success.default,
        '&&&': {
          color: theme.palette.text.success.onSuccess,
        },
      }),
    },
    {
      props: { color: 'error' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.default,
        '&&&': {
          color: theme.palette.text.danger.onDanger,
        },
      }),
    },
    {
      props: { color: 'warning' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.warning.default,
        '&&&': {
          color: theme.palette.text.warning.onWarning,
        },
      }),
    },
    {
      props: { color: 'info' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.info.default,
        '&&&': {
          color: theme.palette.text.info.onInfo,
        },
      }),
    },
    {
      props: { color: 'neutralStrong' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.inverted,
        '&&&': {
          color: theme.palette.text.inverted,
        },
      }),
    },
    {
      props: { color: 'ai' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.ai.default,
        '&&&': {
          color: theme.palette.text.ai.onAi,
        },
      }),
    },
    {
      props: { color: 'bronze' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.bronze.default,
        '&&&': {
          color: theme.palette.text.utility.bronze['on-bronze'],
        },
      }),
    },
    {
      props: { color: 'gold' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.gold.default,
        '&&&': {
          color: theme.palette.text.utility.gold['on-gold'],
        },
      }),
    },
    {
      props: { color: 'orange' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.orange.default,
        '&&&': {
          color: theme.palette.text.utility.orange['on-orange'],
        },
      }),
    },
    {
      props: { color: 'lime' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.lime.default,
        '&&&': {
          color: theme.palette.text.utility.lime['on-lime'],
        },
      }),
    },
    {
      props: { color: 'mint' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.mint.default,
        '&&&': {
          color: theme.palette.text.utility.mint['on-mint'],
        },
      }),
    },
    {
      props: { color: 'pink' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.pink.default,
        '&&&': {
          color: theme.palette.text.utility.pink['on-pink'],
        },
      }),
    },
    {
      props: { color: 'plum' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.utility.plum.default,
        '&&&': {
          color: theme.palette.text.utility.plum['on-plum'],
        },
      }),
    },
  ],
}
